// @flow
import { get, values, size } from 'lodash';
import moment from 'moment';

export type BalanceStatus = 'due' | 'settled' | 'credit';

// The API's balance is payments minus services (Patient#update_balance): below zero the patient owes the clinic,
// above zero the patient has paid ahead. Under half a cent is zero (the balance is a float).
export const balanceStatus = (balance: ?(number | string)): ?BalanceStatus => {
  const value = parseFloat(balance);
  if (Number.isNaN(value)) return null;
  if (value <= -0.005) return 'due';
  if (value >= 0.005) return 'credit';
  return 'settled';
};

// What counts toward the balance (Patient#sum_of_services / #sum_of_payments): a contracted service, and a payment
// that isn't "Em débito". The rest is listed muted.
export const countsTowardBalance = (kind: 'service' | 'payment', status: ?string): boolean => (
  kind === 'service' ? status === 'confirmed' : status === 'verified' || status === 'unverified'
);

export type Movement = {
  kind: 'service' | 'payment',
  key: string,
  item: Object,
};

const day = (date: ?string) => {
  const parsed = moment(date, 'YYYY-MM-DD');
  return parsed.isValid() ? parsed.valueOf() : -Infinity;
};

// The patient's services and payments in one list, newest first. On the same day a payment comes first (it
// usually settles what was contracted that day), then the newest of each kind (the higher id).
export const recentMovements = (services: ?Object, payments: ?Object): Array<Movement> => {
  const movements = [
    ...values(services || {}).filter(Boolean).map((item) => ({ kind: 'service', key: `service-${item.id}`, item })),
    ...values(payments || {}).filter(Boolean).map((item) => ({ kind: 'payment', key: `payment-${item.id}`, item })),
  ];
  return movements.sort((a, b) => (
    day(b.item.date) - day(a.item.date)
    || (a.kind === b.kind ? 0 : (a.kind === 'payment' ? -1 : 1))
    || Number(b.item.id) - Number(a.item.id)
  ));
};

// The newest date of a list, for the "último em" of its row
export const latestDate = (items: ?Object): ?string => {
  let latest = null;
  values(items || {}).forEach((item) => {
    const date = get(item, 'date');
    if (date && day(date) > day(latest)) latest = date;
  });
  return latest;
};

// The "último em" date: day and month this year (fits a 320px row), the year too before that
export const shortDate = (date: ?string, now: Object = moment()): string => {
  const parsed = moment(date, 'YYYY-MM-DD');
  if (!parsed.isValid()) return '';
  return parsed.format(parsed.year() === now.year() ? 'DD/MM' : 'DD/MM/YYYY');
};

// A list of the overview: still on its way (nothing to show yet), failed to load (nothing to show), or ready.
// loadFailed, not errors: a failed save sets errors too.
export const listState = (list: ?Object): 'loading' | 'error' | 'ready' => {
  const hasData = size(get(list, 'data')) > 0;
  if (hasData) return 'ready';
  if (get(list, 'isLoading')) return 'loading';
  if (get(list, 'loadFailed')) return 'error';
  return 'ready';
};
